
import { queryClient } from "./queryClient";

export interface LiveConnection {
  close: () => void;
  socket?: WebSocket | null;
  isConnected: () => boolean;
}

let singleton: LiveConnection | null = null;

function deriveWsUrl(): string | null {
  const explicit = import.meta.env.VITE_WS_URL as string | undefined;
  if (explicit) return explicit;
  const api = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (!api) return null;
  try {
    const u = new URL(api);
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    // convention: /ws/market
    u.pathname = (u.pathname.replace(/\/$/, '')) + '/ws/market';
    return u.toString();
  } catch {
    return null;
  }
}

export function connectLiveMarket(onStatus?: (s: 'connecting' | 'open' | 'closed' | 'error') => void): LiveConnection | null {
  if (singleton && singleton.isConnected()) return singleton;
  const url = deriveWsUrl();
  if (!url) return null;
  let ws: WebSocket | null = null;
  let closed = false;

  const attempt = () => {
    if (closed) return;
    onStatus?.('connecting');
    ws = new WebSocket(url);
    ws.onopen = () => {
      onStatus?.('open');
      // Optionally request an initial snapshot (if backend supports)
      try { ws?.send(JSON.stringify({ action: 'subscribe', topics: ['market'] })); } catch {}
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        handleMessage(msg);
      } catch (e) {
        console.warn('[liveMarket] invalid message', e);
      }
    };
    ws.onerror = () => {
      onStatus?.('error');
    };
    ws.onclose = () => {
      onStatus?.('closed');
      if (!closed) setTimeout(attempt, 4000); // auto-retry
    };
  };

  attempt();

  singleton = {
    close: () => { closed = true; try { ws?.close(); } catch {} },
    socket: ws,
    isConnected: () => !!ws && ws.readyState === WebSocket.OPEN,
  };
  return singleton;
}

function handleMessage(msg: any) {
  if (!msg || typeof msg !== 'object') return;
  const { type } = msg;
  switch (type) {
    case 'market_health':
      queryClient.setQueryData(["/api/market/health"], msg.data);
      break;
    case 'session':
      queryClient.setQueryData(["/api/market/session"], msg.data);
      break;
    case 'gate_decision':
      queryClient.setQueryData(["/api/market/pre-analysis"], msg.data);
      break;
    case 'core_assets':
      // we stored as { core_assets: {...} }
      queryClient.setQueryData(["/api/market/core-assets", "BTCUSDT,ETHUSDT"], { core_assets: msg.data });
      break;
    case 'orderbook':
      if (msg.symbol) {
        queryClient.setQueryData(["/api/market/orderbook", msg.symbol], msg.data);
      }
      break;
    case 'movers':
      queryClient.setQueryData(["/api/market/movers", { full: true }], { movers: msg.data });
      break;
    default:
      break;
  }
}

export function useLiveMarket() {
  // Lightweight hook just to initiate connection once a component needs it.
  // Intentionally not using dependency arrays beyond initial mount.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  if (typeof window === 'undefined') return;
  // dynamic import not necessary; simple singleton connect
  connectLiveMarket();
}
